import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, RotateCcw, Clock } from 'lucide-react';
import { DeepSeekService } from '../services/deepseekService';
import { TranscriptionDisplay } from './TranscriptionDisplay';

export const TranscriptionFull = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null); // URL do áudio para o player
  const [transcription, setTranscription] = useState('');
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(0); // cronômetro

  const service = new DeepSeekService();
  const mediaRecorderRef = useRef(null);
  const intervalRef = useRef(null);
  const chunksRef = useRef([]);

  /* ---------- CRONÔMETRO ---------- */
  useEffect(() => {
    if (isRecording) {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRecording]);

  /* ---------- LIMPEZA DE URL AO DESMONTAR ---------- */
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  /* ---------- GRAVAÇÃO ---------- */
  const startRecording = async () => {
    if (!navigator.mediaDevices) {
      alert('Microfone não disponível. Use localhost ou HTTPS.');
      return;
    }

    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType: mime });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Parar todas as trilhas do stream
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      mediaRecorderRef.current = mediaRecorder;
    } catch (err) {
      console.error('Erro ao acessar o microfone:', err);
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  /* ---------- LIMPAR TUDO ---------- */
  const resetAll = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setAudioBlob(null);
    setTranscription('');
    setSeconds(0);
  };

  /* ---------- PROCESSAR TRANSCRIÇÃO ---------- */
  const processTranscription = async () => {
    if (!audioBlob) return;
    setLoading(true);
    try {
      const { text } = await service.transcribeAudio(audioBlob);
      const formatted = await service.formatTranscription(text);
      setTranscription(formatted);
    } catch (err) {
      setTranscription(`Erro ao transcrever: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="transcription-full" style={{ padding: '24px', fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center', color: '#1f2937' }}>Transcritor Médico IA</h2>

      {/* INSTRUÇÃO */}
      <p style={{ marginBottom: '16px', textAlign: 'center', color: '#4b5563', lineHeight: '1.5' }}>
        {isRecording
          ? '🎙️ Gravando consulta... fale normalmente.'
          : 'Clique em “Iniciar” para gravar a consulta. O áudio será convertido em história clínica estruturada.'}
      </p>

      {/* CONTROLES + CRONÔMETRO */}
      <div
        className="controls"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        {!isRecording ? (
          <button
            className="btn-primary"
            onClick={startRecording}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
          >
            <Mic size={20} /> Iniciar
          </button>
        ) : (
          <>
            <button
              className="btn-stop"
              onClick={stopRecording}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}
            >
              <Square size={20} /> Parar
            </button>

            {/* CRONÔMETRO */}
            <div
              className="timer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#dc2626',
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              <Clock size={18} />
              <span>
                {String(Math.floor(seconds / 60)).padStart(2, '0')}:
                {String(seconds % 60).padStart(2, '0')}
              </span>
            </div>
          </>
        )}
      </div>

      {/* PLAYER DE ÁUDIO (após gravação) */}
      {audioUrl && (
        <div
          className="recorded-audio"
          style={{
            margin: '20px 0',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e5e7eb',
          }}
        >
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              color: '#374151',
              fontWeight: '500',
            }}
          >
            Áudio gravado:
          </label>
          <audio
            src={audioUrl}
            controls
            style={{
              width: '100%',
              maxWidth: '320px',
              margin: '8px auto',
              borderRadius: '6px',
            }}
            onPlay={() => console.log('Reproduzindo áudio...')}
          />
        </div>
      )}

      {/* BOTÕES: GERAR TRANSCRIÇÃO E LIMPAR */}
      {audioBlob && (
        <div
          className="action-row"
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            marginTop: '20px',
          }}
        >
          <button
            className="btn-accent"
            onClick={processTranscription}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {loading ? 'Processando...' : '📄 Gerar Transcrição'}
          </button>

          <button
            className="btn-outline"
            onClick={resetAll}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#374151',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
            }}
          >
            <RotateCcw size={18} /> Limpar
          </button>
        </div>
      )}

      {/* EXIBIÇÃO DA TRANSCRIÇÃO */}
      {transcription && (
        <div className="transcription-output" style={{ marginTop: '24px' }}>
          <TranscriptionDisplay transcription={transcription} />
        </div>
      )}
    </div>
  );
};

export default TranscriptionFull;
